import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

const COLLECTIONS_TABLES = ["user", "collection"] as const;

export function useGetCollectionsQueryKey() {
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const collectionsQueryOptions = actorQueries(
    import.meta.location,
  ).collection.findMany.useQueryOptions({});
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(COLLECTIONS_TABLES));

  return () =>
    withTableChangeVersion([
      "collections",
      { actorUserDataQueryOptions, collectionsQueryOptions },
    ] as const);
}

export const [useCollectionsQueryOptions, collectionsQueryFn] = createUseQueryOptions(
  useGetCollectionsQueryKey,
  async ({ client, queryKey: [_, { actorUserDataQueryOptions, collectionsQueryOptions }] }) => {
    const {
      user: { id: userId },
    } = await client.ensureQueryData(actorUserDataQueryOptions);
    const collections = await client.fetchQuery(collectionsQueryOptions);

    // The Favorites singleton is included regardless of its `userId`: during a sign-in/guest-
    // conversion bootstrap the `user` row is swapped to the account's canonical id while the
    // Favorites row (created earlier under the transient local id) is only re-owned once the
    // ensure-query's adoption pass runs — a userId-only filter hides it for that whole window
    // (run 29629279241 iOS deck-collection-create: "Open Favorites" missing from Collections).
    // At most one Favorites row exists per actor DB, and sign-in wipes the DB, so a foreign-owned
    // row only ever exists transiently mid-bootstrap — exactly when it should still be shown.
    return collections.filter(
      (collection) => collection.userId === userId || collection.isFavorites,
    );
  },
);

// Scoping by `userId` uses `collection_user_id_idx` instead of a full scan.
// Soft-delete filter is injected by the wrapped `actorDb` query proxy.
